/**
 * Agent dashboard — `GET reports/dashboard/`, same query key
 * (`['reports-dashboard']`) `ReportsDashboardPage.tsx` uses so TanStack Query
 * dedupes if a user visits both. This route only mounts for AGENT
 * (router.tsx), so the manager response shape is the only one reachable
 * here — `isTenantDashboard` still guards it to satisfy the `Dashboard`
 * union type, not because the tenant branch is really reachable.
 *
 * Icon-badged stat tiles (`StatTile`) plus a "Needs attention" panel
 * (`DashboardPanel`), same shape as `LandlordHome.tsx` — see that file for
 * the shared pattern's rationale. No "rent collected this month" tile here:
 * that one's client-side pagination walk is landlord-specific scope, not
 * part of the shared pattern.
 */
import { useQuery } from '@tanstack/react-query';
import { Column, Grid } from '@carbon/react';
import { Building, Home, UserMultiple, Money, ToolBox, Receipt } from '@carbon/icons-react';
// DashboardPanel's `emptyIcon` is still typed `LucideIcon` — its own Carbon
// conversion is deferred to Phase 3 (see CARBON_MIGRATION.md), so this one
// icon stays lucide until then rather than half-converting that component.
import { CheckCircle2 } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { getDashboard } from '../../api/endpoints/reports';
import { getOverdueInvoices } from '../../api/endpoints/payments';
import { listMaintenanceRequests } from '../../api/endpoints/maintenance';
import { isTenantDashboard } from '../../types/report';
import { StatTile } from '../../components/StatTile';
import { DashboardPanel, type DashboardPanelRow } from '../../components/DashboardPanel';

export function AgentHome(): React.JSX.Element {
  const query = useQuery({
    queryKey: ['reports-dashboard'],
    queryFn: () => getDashboard(),
  });

  const overdueInvoices = useQuery({
    queryKey: ['overdue-invoices'],
    queryFn: () => getOverdueInvoices(),
  });

  const urgentMaintenance = useQuery({
    queryKey: ['maintenance-requests', { priority: 'URGENT' }],
    queryFn: () => listMaintenanceRequests({ priority: 'URGENT' }),
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const dashboard = query.data;
  if (isTenantDashboard(dashboard)) return <></>;

  const openMaintenance = dashboard.maintenance.new + dashboard.maintenance.in_progress;

  const overdueRows: DashboardPanelRow[] = (overdueInvoices.data ?? []).map((invoice) => ({
    id: invoice.id,
    to: `/agent/billing/${invoice.id}`,
    label: `${invoice.tenant_name} — ${invoice.unit_number}`,
    value: `${invoice.amount_due} due ${invoice.due_date}`,
  }));

  const urgentRows: DashboardPanelRow[] = (urgentMaintenance.data?.results ?? []).map((request) => ({
    id: request.id,
    to: `/agent/maintenance/${request.id}`,
    label: request.title,
    value: `${request.property_name} — ${request.unit_number}`,
  }));

  return (
    <section className="agent-home">
      <h2>Agent Dashboard</h2>
      <div className="dashboard-stats">
        <Grid withRowGap>
          <Column sm={4} md={4} lg={4}>
            <StatTile icon={Building} to="/agent/properties" value={dashboard.properties.total} label="Properties" />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Home}
              to="/agent/properties"
              value={dashboard.units.vacant}
              label="Vacant units"
              detail={`${dashboard.units.occupied} occupied`}
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={UserMultiple}
              to="/agent/tenants/leases"
              value={dashboard.leases.active}
              label="Active leases"
              detail={
                dashboard.leases.expiring_soon > 0
                  ? `${dashboard.leases.expiring_soon} expiring soon`
                  : undefined
              }
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Money}
              to="/agent/billing/overdue"
              value={dashboard.payments.overdue}
              label="Overdue invoices"
              detail={`${dashboard.payments.overdue_amount} overdue`}
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={ToolBox}
              to="/agent/maintenance"
              value={openMaintenance}
              label="Open maintenance requests"
              detail={dashboard.maintenance.urgent > 0 ? `${dashboard.maintenance.urgent} urgent` : undefined}
            />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile icon={Money} to="/agent/billing" value={dashboard.payments.total_due} label="Total due" />
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={Receipt}
              to="/agent/expenses"
              value={dashboard.expenses.this_month}
              label="Expenses this month"
            />
          </Column>
        </Grid>
      </div>

      <DashboardPanel
        title="Overdue payments"
        rows={overdueRows}
        emptyIcon={CheckCircle2}
        emptyMessage="All payments are up to date!"
      />

      <DashboardPanel
        title="Urgent maintenance"
        rows={urgentRows}
        emptyIcon={CheckCircle2}
        emptyMessage="No urgent maintenance requests."
      />
    </section>
  );
}

export default AgentHome;
